import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CheckmarkCircle20Regular,
  Delete20Regular,
  DocumentText20Regular,
  ArrowUndo20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { ListToolbar } from "../../components/ListToolbar";
import { Modal } from "../../components/Modal";
import { formatCurrency, formatDateShort } from "../Obras/obraFormat";
import {
  deleteRentReceipt,
  getRentReceipts,
  setRentReceiptStatus,
  type RentReceipt,
} from "../../recibos/recibosApi";

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

// Cor por estado do recibo — classes já compiladas em index.css (grep-verificadas).
const STATUS_BADGE: Record<string, string> = {
  rascunho: "bg-yellow-500/10 text-yellow-700",
  emitido: "bg-green-500/10 text-green-700",
};

const statusBadgeClass = (status: string): string =>
  STATUS_BADGE[status] ?? "bg-muted text-secondary-text";

const STATUS_LABEL: Record<string, string> = {
  rascunho: "Rascunho",
  emitido: "Emitido",
};

const statusLabel = (status: string): string => STATUS_LABEL[status] ?? status;

const MONTH_NAMES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const STATUS_CHIPS = [
  { code: "", name: "Todos" },
  { code: "rascunho", name: "Rascunho" },
  { code: "emitido", name: "Emitido" },
];

type ToastKind = "success" | "error";

export function RecibosView() {
  const now = new Date();
  const currentYear = now.getFullYear();

  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState<number>(0); // 0 = todos os meses

  const [receipts, setReceipts] = useState<RentReceipt[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [filter, setFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [busyId, setBusyId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [detail, setDetail] = useState<RentReceipt | null>(null);

  const [toast, setToast] = useState<{ kind: ToastKind; message: string } | null>(
    null,
  );
  const toastTimeoutRef = useRef<number | null>(null);

  const showToast = useCallback((kind: ToastKind, message: string) => {
    setToast({ kind, message });
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, 4000);
  }, []);

  useEffect(
    () => () => {
      if (toastTimeoutRef.current) {
        window.clearTimeout(toastTimeoutRef.current);
      }
    },
    [],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getRentReceipts({
        year,
        month: month > 0 ? month : undefined,
      });
      setReceipts(data);
    } catch (err) {
      setError(errorMessage(err, "Não foi possível carregar os recibos."));
    } finally {
      setIsLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSetStatus = useCallback(
    async (receipt: RentReceipt, status: string) => {
      setBusyId(receipt.id);
      try {
        const updated = await setRentReceiptStatus(receipt.id, status);
        setReceipts((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        setDetail((prev) => (prev && prev.id === updated.id ? updated : prev));
        showToast(
          "success",
          updated.status === "emitido"
            ? "Recibo marcado como emitido."
            : "Recibo reaberto como rascunho.",
        );
      } catch (err) {
        showToast("error", errorMessage(err, "Não foi possível atualizar o recibo."));
      } finally {
        setBusyId(null);
      }
    },
    [showToast],
  );

  const handleDelete = useCallback(
    async (receipt: RentReceipt) => {
      setBusyId(receipt.id);
      try {
        await deleteRentReceipt(receipt.id);
        setReceipts((prev) => prev.filter((r) => r.id !== receipt.id));
        setConfirmDeleteId(null);
        showToast("success", "Recibo apagado.");
      } catch (err) {
        showToast("error", errorMessage(err, "Não foi possível apagar o recibo."));
      } finally {
        setBusyId(null);
      }
    },
    [showToast],
  );

  const normalizedFilter = filter.trim().toLowerCase();
  const shownReceipts = useMemo(() => {
    return receipts.filter((r) => {
      if (statusFilter !== "" && r.status !== statusFilter) return false;
      if (normalizedFilter === "") return true;
      return [r.receiptNumber, r.propertyName, r.tenantName, r.periodLabel]
        .join(" ")
        .toLowerCase()
        .includes(normalizedFilter);
    });
  }, [receipts, statusFilter, normalizedFilter]);

  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let y = currentYear - 3; y <= currentYear + 1; y += 1) {
      years.push(y);
    }
    return years;
  }, [currentYear]);

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Recibos"
        subtitle="Recibos de renda — rascunhos gerados ao confirmar o pagamento"
      />

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        {error && (
          <div
            className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {error}
          </div>
        )}

        {/* Filtros de período (ano / mês) */}
        <div className="flex items-end gap-3 flex-wrap">
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Ano
            <select
              value={year}
              onChange={(event) => setYear(Number(event.target.value))}
              className="px-3 py-2 border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Mês
            <select
              value={month}
              onChange={(event) => setMonth(Number(event.target.value))}
              className="px-3 py-2 border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              <option value={0}>Todos</option>
              {MONTH_NAMES.map((name, index) => (
                <option key={name} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <ListToolbar
          filter={filter}
          onFilterChange={setFilter}
          placeholder="Filtrar por número, imóvel, inquilino ou período…"
          summary={
            <span className="text-secondary-text text-sm">
              {shownReceipts.length} recibo(s)
            </span>
          }
        />

        <div className="flex items-center gap-2 flex-wrap">
          {STATUS_CHIPS.map((chip) => {
            const isActive = statusFilter === chip.code;
            return (
              <button
                key={chip.code || "all"}
                type="button"
                onClick={() => setStatusFilter(chip.code)}
                className={`inline-flex items-center px-3 py-1 text-sm cursor-pointer ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-foreground hover:bg-accent/50"
                }`}
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {chip.name}
              </button>
            );
          })}
        </div>

        <div
          className="bg-card border border-border overflow-x-auto"
          style={{ borderRadius: "var(--radius-card)" }}
        >
          <table className="w-full" style={{ minWidth: 980 }}>
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-4 py-3" style={{ width: 150 }}>
                  Número
                </th>
                <th className="px-4 py-3">Imóvel</th>
                <th className="px-4 py-3">Inquilino</th>
                <th className="px-4 py-3" style={{ width: 130 }}>
                  Período
                </th>
                <th className="px-4 py-3" style={{ width: 130 }}>
                  Pagamento
                </th>
                <th className="px-4 py-3 text-right" style={{ width: 110 }}>
                  Valor
                </th>
                <th className="px-4 py-3" style={{ width: 110 }}>
                  Estado
                </th>
                <th className="px-4 py-3" style={{ width: 260 }}>
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-6 text-center text-secondary-text"
                  >
                    A carregar…
                  </td>
                </tr>
              ) : shownReceipts.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-6 text-center text-secondary-text"
                  >
                    {filter !== "" || statusFilter !== ""
                      ? "Sem recibos para o filtro."
                      : "Sem recibos para o período. Os recibos são criados ao marcar uma renda como paga."}
                  </td>
                </tr>
              ) : (
                shownReceipts.map((r) => {
                  const isBusy = busyId === r.id;
                  return (
                    <tr
                      key={r.id}
                      className="border-b border-border last:border-b-0 hover:bg-accent/30"
                    >
                      <td
                        className="px-4 py-3 whitespace-nowrap"
                        style={{ fontWeight: "var(--font-weight-medium)" }}
                      >
                        {r.receiptNumber}
                      </td>
                      <td className="px-4 py-3 break-words">{r.propertyName}</td>
                      <td className="px-4 py-3 break-words">{r.tenantName}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {r.periodLabel}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {formatDateShort(r.paidDate)}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {formatCurrency(r.amount)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs ${statusBadgeClass(
                            r.status,
                          )}`}
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          {statusLabel(r.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {confirmDeleteId === r.id ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-secondary-text">
                              Apagar?
                            </span>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => handleDelete(r)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-destructive/10 text-destructive cursor-pointer disabled:opacity-50"
                              style={{ borderRadius: "var(--radius)" }}
                            >
                              Confirmar
                            </button>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => setConfirmDeleteId(null)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border text-foreground hover:bg-accent/50 cursor-pointer disabled:opacity-50"
                              style={{ borderRadius: "var(--radius)" }}
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 flex-wrap">
                            <button
                              type="button"
                              onClick={() => setDetail(r)}
                              className="inline-flex items-center gap-1 text-primary text-sm cursor-pointer"
                            >
                              <DocumentText20Regular className="w-4 h-4" />
                              Ver
                            </button>
                            {r.status === "emitido" ? (
                              <button
                                type="button"
                                disabled={isBusy}
                                onClick={() => handleSetStatus(r, "rascunho")}
                                className="inline-flex items-center gap-1 text-secondary-text text-sm cursor-pointer disabled:opacity-50"
                              >
                                <ArrowUndo20Regular className="w-4 h-4" />
                                Reabrir
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={isBusy}
                                onClick={() => handleSetStatus(r, "emitido")}
                                className="inline-flex items-center gap-1 text-primary text-sm cursor-pointer disabled:opacity-50"
                              >
                                <CheckmarkCircle20Regular className="w-4 h-4" />
                                Marcar emitido
                              </button>
                            )}
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => setConfirmDeleteId(r.id)}
                              className="inline-flex items-center gap-1 text-destructive text-sm cursor-pointer disabled:opacity-50"
                            >
                              <Delete20Regular className="w-4 h-4" />
                              Apagar
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <Modal
          title={`Recibo ${detail.receiptNumber}`}
          onClose={() => setDetail(null)}
          maxWidthClass="max-w-2xl"
          footer={
            <div className="flex items-center gap-2">
              {detail.status !== "emitido" && (
                <button
                  type="button"
                  disabled={busyId === detail.id}
                  onClick={() => handleSetStatus(detail, "emitido")}
                  className="px-4 py-2 min-h-[44px] bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
                  style={{ borderRadius: "var(--radius-button)" }}
                >
                  Marcar emitido
                </button>
              )}
              <button
                type="button"
                onClick={() => setDetail(null)}
                className="px-4 py-2 min-h-[44px] border border-border"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Fechar
              </button>
            </div>
          }
        >
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span
              className={`inline-flex items-center px-2 py-1 text-xs ${statusBadgeClass(
                detail.status,
              )}`}
              style={{ borderRadius: "var(--radius)" }}
            >
              {statusLabel(detail.status)}
            </span>
            <span className="text-sm text-secondary-text">
              Emitido em {formatDateShort(detail.issueDate)}
            </span>
          </div>

          <div
            className="border border-border bg-muted p-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            <div>
              <span className="text-secondary-text">Senhorio</span>
              <p style={{ fontWeight: "var(--font-weight-medium)" }}>
                {detail.landlordName}
              </p>
              {detail.landlordTaxId && (
                <p className="text-xs text-secondary-text">
                  NIF {detail.landlordTaxId}
                </p>
              )}
            </div>
            <div>
              <span className="text-secondary-text">Inquilino</span>
              <p style={{ fontWeight: "var(--font-weight-medium)" }}>
                {detail.tenantName}
              </p>
              <p className="text-xs text-secondary-text">
                NIF {detail.tenantTaxId ?? "—"}
              </p>
            </div>
            <div className="md:col-span-2">
              <span className="text-secondary-text">Imóvel</span>
              <p style={{ fontWeight: "var(--font-weight-medium)" }}>
                {detail.propertyName}
              </p>
              {detail.propertyAddress && (
                <p className="text-xs text-secondary-text">
                  {detail.propertyAddress}
                </p>
              )}
            </div>
            <div>
              <span className="text-secondary-text">Período</span>
              <p>{detail.periodLabel}</p>
            </div>
            <div>
              <span className="text-secondary-text">Data de pagamento</span>
              <p>{formatDateShort(detail.paidDate)}</p>
            </div>
            <div>
              <span className="text-secondary-text">Valor</span>
              <p style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
                {formatCurrency(detail.amount)}
              </p>
            </div>
          </div>

          {detail.notes && (
            <div className="text-sm">
              <span className="text-secondary-text">Notas</span>
              <p className="whitespace-pre-wrap">{detail.notes}</p>
            </div>
          )}

          <p
            className="text-secondary-text"
            style={{ fontSize: "var(--text-caption)" }}
          >
            Rascunho preparado para emissão oficial (Portal das Finanças). A emissão
            legal é feita fora da aplicação.
          </p>
        </Modal>
      )}

      {toast && (
        <div
          className={`fixed border border-border px-4 py-3 text-sm ${
            toast.kind === "success"
              ? "bg-green-500/10 text-green-700"
              : "bg-destructive/10 text-destructive"
          }`}
          style={{
            bottom: "1.5rem",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 50,
            borderRadius: "var(--radius)",
          }}
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
